import { useState, type FormEvent, type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { changePasswordSchema } from '@kn/shared';
import { useAuth } from '../auth/auth-context';
import { messageErreur } from '../api/error-message';
import { useTitrePage } from '../hooks/useTitrePage';

export function ChangePasswordPage(): ReactElement {
  // Hors de la coquille : la page ecrit elle-meme son titre.
  useTitrePage('Changer le mot de passe');
  const { user, changePassword } = useAuth();

  const [ancien, setAncien] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [succes, setSucces] = useState(false);

  if (!user) {
    return <Navigate to="/connexion" replace />;
  }

  if (succes && !user.mustChangePassword) {
    return <Navigate to="/tableau-de-bord" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setErreur(null);

    if (nouveau !== confirmation) {
      setErreur('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }

    const parsed = changePasswordSchema.safeParse({
      currentPassword: ancien,
      newPassword: nouveau,
    });
    if (!parsed.success) {
      setErreur(parsed.error.issues[0]?.message ?? 'Formulaire invalide.');
      return;
    }

    setEnCours(true);
    try {
      await changePassword(parsed.data);
      setSucces(true);
    } catch (error) {
      setErreur(messageErreur(error));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="page-connexion">
      <div className="carte page-connexion__carte">
        <h1>Changer le mot de passe</h1>
        {user.mustChangePassword ? (
          <p className="alerte alerte--info">
            Pour des raisons de sécurité, vous devez definir un nouveau mot de passe avant
            d&apos;accéder a l&apos;application.
          </p>
        ) : (
          <p className="sous-titre">Définissez un nouveau mot de passe pour votre compte.</p>
        )}

        {erreur && (
          <p className="alerte alerte--erreur" role="alert">
            {erreur}
          </p>
        )}

        <form onSubmit={(event) => void handleSubmit(event)} noValidate>
          <label className="champ" htmlFor="ancien">
            <span>Mot de passe actuel</span>
            <input
              id="ancien"
              type="password"
              autoComplete="current-password"
              value={ancien}
              onChange={(event) => setAncien(event.target.value)}
            />
          </label>

          <label className="champ" htmlFor="nouveau">
            <span>Nouveau mot de passe</span>
            <input
              id="nouveau"
              type="password"
              autoComplete="new-password"
              value={nouveau}
              onChange={(event) => setNouveau(event.target.value)}
            />
          </label>

          <label className="champ" htmlFor="confirmation">
            <span>Confirmer le nouveau mot de passe</span>
            <input
              id="confirmation"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </label>

          <p className="sous-titre">
            Au moins 12 caractères, dont une minuscule, une majuscule et un chiffre.
          </p>

          <button type="submit" className="bouton" disabled={enCours}>
            {enCours ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </form>
      </div>
    </div>
  );
}
